import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { sttWebSocket, encodeWAV } from './sttWebSocket';

const SAMPLE_RATE = 16000;
const MAX_RECORDING_MS = 10000;

// Map app language codes to BCP-47 for browser SpeechRecognition.
const LANG_MAP: Record<string, string> = {
    en: 'en-US',
    de: 'de-DE',
    ru: 'ru-RU',
    pl: 'pl-PL',
    es: 'es-ES',
    fr: 'fr-FR',
};

interface UseSpeechInputOptions {
    onResult: (text: string) => void;
    onError?: (err: string) => void;
}

interface UsePronunciationRecorderOptions {
    onAudio: (blob: Blob) => void;
    onError?: (err: string) => void;
}

// Shared PCM capture primitive used by both usePronunciationRecorder and startBackendRecording.
function usePCMRecorder(onWav: (wav: ArrayBuffer) => void, onError?: (msg: string) => void) {
    const [isListening, setIsListening] = useState(false);
    const audioContextRef = useRef<AudioContext | null>(null);
    const processorRef = useRef<ScriptProcessorNode | null>(null);
    const streamRef = useRef<MediaStream | null>(null);
    const pcmBuffersRef = useRef<Int16Array[]>([]);
    const maxTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const cancelledRef = useRef(false);
    const onWavRef = useRef(onWav);
    useEffect(() => { onWavRef.current = onWav; }, [onWav]);

    const stop = useCallback(() => {
        cancelledRef.current = true;
        if (maxTimerRef.current) { clearTimeout(maxTimerRef.current); maxTimerRef.current = null; }
        processorRef.current?.disconnect();
        processorRef.current = null;
        streamRef.current?.getTracks().forEach(t => t.stop());
        streamRef.current = null;
        audioContextRef.current?.close();
        audioContextRef.current = null;
        setIsListening(false);
        const buffers = pcmBuffersRef.current;
        pcmBuffersRef.current = [];
        if (buffers.length > 0) {
            onWavRef.current(encodeWAV(buffers, SAMPLE_RATE));
        }
    }, []);

    const start = useCallback(async () => {
        if (isListening) return;
        cancelledRef.current = false;
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: { sampleRate: SAMPLE_RATE, channelCount: 1, echoCancellation: true },
            });
            if (cancelledRef.current) { stream.getTracks().forEach(t => t.stop()); return; }
            streamRef.current = stream;
            const ctx = new AudioContext({ sampleRate: SAMPLE_RATE });
            audioContextRef.current = ctx;
            const source = ctx.createMediaStreamSource(stream);
            // ScriptProcessor is deprecated but universally supported without extra setup.
            const processor = ctx.createScriptProcessor(4096, 1, 1);
            processorRef.current = processor;
            pcmBuffersRef.current = [];
            processor.onaudioprocess = (e) => {
                const float32 = e.inputBuffer.getChannelData(0);
                const int16 = new Int16Array(float32.length);
                for (let i = 0; i < float32.length; i++) {
                    int16[i] = Math.max(-32768, Math.min(32767, Math.round(float32[i] * 32767)));
                }
                pcmBuffersRef.current.push(int16);
            };
            source.connect(processor);
            processor.connect(ctx.destination);
            setIsListening(true);
            maxTimerRef.current = setTimeout(() => stop(), MAX_RECORDING_MS);
        } catch (err: any) {
            onError?.(err?.message || 'Microphone access denied');
        }
    }, [isListening, stop, onError]);

    return { start, stop, isListening };
}

export function usePronunciationRecorder({ onAudio, onError }: UsePronunciationRecorderOptions) {
    const onAudioRef = useRef(onAudio);
    useEffect(() => { onAudioRef.current = onAudio; }, [onAudio]);
    return usePCMRecorder(
        (wav) => onAudioRef.current(new Blob([wav], { type: 'audio/wav' })),
        onError,
    );
}

export function useSpeechInput({ onResult, onError }: UseSpeechInputOptions) {
    const auth = useAuth();
    const useBackend = auth?.userSettings?.integration_settings?.stt_record ?? false;
    const lang = auth?.userSettings?.learning_language || 'en';
    const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

    const [isListening, setIsListening] = useState(false);

    const onResultRef = useRef(onResult);
    useEffect(() => { onResultRef.current = onResult; }, [onResult]);

    // Browser Web Speech API refs
    const recognitionRef = useRef<any>(null);

    const browserSRSupported = !!(
        (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition
    );
    const isSupported = useBackend || browserSRSupported;

    // Connect STT WebSocket when backend mode is active
    useEffect(() => {
        if (!useBackend) return;

        const token = localStorage.getItem('token') || '';
        if (!sttWebSocket.isConnected()) {
            sttWebSocket.connect(baseURL, token, lang);
        }

        const handler = (event: any) => {
            if (event.type === 'transcript') {
                setIsListening(false);
                onResultRef.current(event.text);
            } else if (event.type === 'error') {
                setIsListening(false);
                onError?.(event.message);
            }
        };
        sttWebSocket.addListener(handler);
        return () => sttWebSocket.removeListener(handler);
    }, [useBackend, baseURL, lang, onResult, onError]);

    const {
        start: startBackendRecording,
        stop: stopBackendRecording,
        isListening: isBackendListening,
    } = usePCMRecorder((wav) => sttWebSocket.sendAudio(wav), onError);

    const startBrowserRecognition = useCallback(() => {
        const SR = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
        if (!SR) return;

        const recognition = new SR();
        recognitionRef.current = recognition;
        recognition.lang = LANG_MAP[lang] || lang;
        recognition.interimResults = false;
        recognition.maxAlternatives = 1;

        recognition.onresult = (e: any) => {
            const transcript = e.results[0][0].transcript;
            setIsListening(false);
            onResultRef.current(transcript);
        };
        recognition.onerror = (e: any) => {
            setIsListening(false);
            onError?.(e.error);
        };
        recognition.onend = () => setIsListening(false);
        recognition.start();
        setIsListening(true);
    }, [lang, onError]); // onResult is read through onResultRef, so it is not a dependency

    const combinedIsListening = useBackend ? isBackendListening : isListening;

    const listen = useCallback(() => {
        if (combinedIsListening) return;
        if (useBackend) {
            startBackendRecording();
        } else {
            startBrowserRecognition();
        }
    }, [combinedIsListening, useBackend, startBackendRecording, startBrowserRecognition]);

    const stopListening = useCallback(() => {
        if (useBackend) {
            stopBackendRecording();
        } else {
            recognitionRef.current?.stop();
        }
    }, [useBackend, stopBackendRecording]);

    return { listen, stopListening, isListening: combinedIsListening, isSupported };
}
